import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Monthly Bookkeeping",
  "Payroll Services",
  "Financial Reporting",
  "Catch-Up & Cleanup",
  "Tax-Ready Prep",
  "Other",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Thanks! We'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32 bg-[#EAE7DC]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#4A5D23] mb-4">
              Let's Talk
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#1A2421] mb-5">
              Ready for books that finally make sense?
            </h2>
            <p className="text-base leading-relaxed text-[#4A4F4D] mb-10 max-w-md">
              Send us a note about your business and we'll get back to you with
              a free, no-pressure consultation, usually within one business
              day.
            </p>

            <div className="space-y-6">
              {[
                {
                  icon: MapPin,
                  label: "Service Area",
                  value: "Based in North Canton, OH",
                },
                {
                  icon: Phone,
                  label: "Phone",
                  value: "(330) 451-6665",
                  href: "tel:+13304516665",
                },
                {
                  icon: Mail,
                  label: "Email",
                  value: "hello@simplybalancedbyjulie.com",
                  href: "mailto:hello@simplybalancedbyjulie.com",
                },
                {
                  icon: Clock,
                  label: "Hours",
                  value: "Mon–Fri · 9am – 5pm ET",
                },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white border border-[#D8D3C8] text-[#4A5D23]">
                    <item.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-[#707573] font-semibold">
                      {item.label}
                    </p>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-base font-medium text-[#1A2421] hover:text-[#4A5D23] transition-colors"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-base font-medium text-[#1A2421]">
                        {item.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-white rounded-2xl border border-[#D8D3C8] p-8 md:p-10 shadow-[0_4px_24px_rgba(28,35,33,0.04)]"
          >
            {done ? (
              <div
                data-testid="contact-success"
                className="flex flex-col items-center text-center py-12"
              >
                <CheckCircle2 className="h-14 w-14 text-[#4A5D23] mb-5" />
                <h3 className="font-heading text-2xl font-medium text-[#1A2421] mb-2">
                  Message sent!
                </h3>
                <p className="text-[#4A4F4D] max-w-xs">
                  Thank you for reaching out. We'll reply within one business
                  day.
                </p>
                <button
                  data-testid="contact-reset"
                  onClick={() => setDone(false)}
                  className="mt-6 text-sm font-semibold text-[#4A5D23] hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-[#1A2421] font-medium">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      data-testid="contact-name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jane Doe"
                      className="bg-[#FAF9F6] border-[#D8D3C8] focus-visible:ring-[#4A5D23]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-[#1A2421] font-medium">
                      Phone
                    </Label>
                    <Input
                      id="phone"
                      data-testid="contact-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="(330) 555-0000"
                      className="bg-[#FAF9F6] border-[#D8D3C8] focus-visible:ring-[#4A5D23]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#1A2421] font-medium">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@business.com"
                    className="bg-[#FAF9F6] border-[#D8D3C8] focus-visible:ring-[#4A5D23]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service" className="text-[#1A2421] font-medium">
                    Service of interest
                  </Label>
                  <select
                    id="service"
                    data-testid="contact-service"
                    value={form.service}
                    onChange={update("service")}
                    className="flex h-10 w-full rounded-md border border-[#D8D3C8] bg-[#FAF9F6] px-3 py-2 text-sm text-[#1A2421] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A5D23]"
                  >
                    <option value="">Select a service…</option>
                    {services.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-[#1A2421] font-medium">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us a bit about your business and what you need help with…"
                    rows={5}
                    className="bg-[#FAF9F6] border-[#D8D3C8] focus-visible:ring-[#4A5D23] resize-none"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#4A5D23] px-8 py-3.5 text-sm font-semibold text-white hover:bg-[#3A4D13] transition-all duration-300 disabled:opacity-60 hover:shadow-lg hover:shadow-[#4A5D23]/20"
                >
                  {loading ? "Sending…" : "Send Message"}
                  {!loading && <Send className="h-4 w-4" />}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
